import { useState } from 'react';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@/components/ui/input-group';

interface Props {
  id?: string;
  /** Adorno fijo: '%' va de sufijo a la derecha, '$' de prefijo a la izquierda — ambos DENTRO del borde del campo. */
  adorno: '%' | '$';
  /** Valor numérico inicial (o null si el campo arranca vacío). Solo se usa al montar. */
  valorInicial: number | null;
  onValueChange: (valor: number | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Formateo es-AR: el monto lleva separador de miles, el % no (dos números
// chicos, tres dígitos rara vez importan, pero así queda pedido).
const FMT_MONTO = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true });
const FMT_PCT = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });

function formatear(n: number, adorno: '%' | '$'): string {
  return (adorno === '$' ? FMT_MONTO : FMT_PCT).format(n);
}

// Normaliza un string tipeado en formato es-AR a un número (o null si queda
// vacío/ilegible). Regla clave de los puntos: en es-AR el punto es separador
// de MILES cuando va seguido de EXACTAMENTE 3 dígitos — "6.600" = seis mil
// seiscientos. Si va seguido de menos de 3 dígitos — "6.60" — no es
// agrupador: es un punto decimal suelto (seis con sesenta, 6,6). Por eso el
// regex de abajo solo saca los puntos que matchean "3 dígitos y después no
// dígito (u fin de string)"; el resto de los puntos (decimales sueltos) los
// deja para que parseFloat los interprete tal cual. Recién después de sacar
// los de miles, la coma remanente (si hay) pasa a ser el separador decimal.
function normalizarNumeroEsAr(raw: string): number | null {
  let limpio = raw.replace(/[%$\s]/g, '').replace(/[a-zA-Z]/g, '');
  if (!limpio) return null;
  limpio = limpio.replace(/\.(?=\d{3}(\D|$))/g, '');
  limpio = limpio.replace(',', '.');
  const n = Number.parseFloat(limpio);
  if (!Number.isFinite(n)) return null;
  // Redondeo a 2 decimales — evita arrastrar errores de punto flotante.
  return Math.round(n * 100) / 100;
}

// Campo de % o monto con adorno FIJO — patrón cerrado con Juan (2026-07-10):
// NADA de máscara en vivo ni input[type=number] (no soporta separador de
// miles ni coma decimal es-AR, y salta el spinner nativo). El usuario tipea
// lo que quiera con libertad; recién al salir del campo (onBlur) se normaliza
// (regla es-AR de arriba) y se re-escribe formateado. El valor numérico ya
// parseado sale por `onValueChange` en cada tecla y otra vez en el blur — el
// string mostrado es un detalle interno de este componente.
export function CampoDecimal({ id, adorno, valorInicial, onValueChange, placeholder, className, disabled }: Props) {
  const [texto, setTexto] = useState(() => (valorInicial != null ? formatear(valorInicial, adorno) : ''));

  function handleBlur(): void {
    const n = normalizarNumeroEsAr(texto);
    setTexto(n != null ? formatear(n, adorno) : '');
    onValueChange(n);
  }

  return (
    <InputGroup className={className}>
      {adorno === '$' && (
        <InputGroupAddon align="inline-start">
          <InputGroupText>$</InputGroupText>
        </InputGroupAddon>
      )}
      <InputGroupInput
        id={id}
        inputMode="decimal"
        value={texto}
        onChange={e => {
          const raw = e.target.value;
          setTexto(raw);
          // Review adversarial v0.12.0 (2026-09-15): el valor parseado también
          // sale EN VIVO, tecla a tecla — no solo en el blur. Sin esto, el
          // padre (EditarChequeoForm, MontoEditForm) seguía viendo el valor
          // viejo mientras el campo tenía el foco, su dirty-check daba «sin
          // cambios» y dejaba Guardar deshabilitado; y un botón nativo
          // disabled no toma el foco ni dispara el blur, así que el primer
          // clic no hacía NADA. El TEXTO mostrado sigue libre hasta el blur
          // (la decisión «nada de máscara en vivo» de julio no cambia): solo
          // se mantiene sincronizado el número del padre.
          onValueChange(normalizarNumeroEsAr(raw));
        }}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="tabular-nums"
      />
      {adorno === '%' && (
        <InputGroupAddon align="inline-end">
          <InputGroupText>%</InputGroupText>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
}
